import HighlightedPips from '@/components/common/HighlightedPips';
import { numberFormatterConfig } from '@/config/config';
import { accumulatePriceBookBidAsks } from '@/helpers/orderbookHelper';
import { useAppSelector } from '@/state/hooks';
import { PairMap } from '@/state/reducers/marketPairSlice';
import { formatNumber } from '@/utils/format';
import useActivePair from '@/utils/hooks/useActivePair';
import { GetPipIndexesReturn, useInstrument } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { PriceLevel } from '@/utils/pricebook';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNumberFormatter } from 'react-aria';

const VerticalBook = () => {
    const lang = useLanguage();
    const activePair = useActivePair();

    const priceBooks = useAppSelector((state) => state.celerMarket.priceBooks[activePair?.celer]);
    const { bids, asks } = useMemo(() => accumulatePriceBookBidAsks(priceBooks), [priceBooks, activePair?.celer]);

    const { formatPip, formatSpread } = useInstrument(activePair?.celer);

    const hasOrderBook = bids && asks && bids[0] && asks[0];

    return (
        <Fragment>
            {!hasOrderBook ? (
                <div className="p-2 italic text-sm text-brand-neutral-300">No orderbook data</div>
            ) : (
                <div className="h-full flex flex-col min-w-[300px]">
                    <div className="flex flex-row p-1 px-2 text-brand-neutral-300 text-sm w-full">
                        <div className="flex-[1_1_33%] text-end">{lang.orderBook.price}</div>
                        <div className="flex-[1_0_33%] text-end">{lang.orderBook.size}</div>
                        <div className="flex-[1_0_33%] text-end">{lang.orderBook.sizeAcc}</div>
                    </div>
                    <div className="order-book flex-1 basis-0 overflow-y-auto divide-y divide-transparent">
                        {asks.map((row, i) => (
                            <OrderBookRow
                                key={i}
                                {...row}
                                type="ask"
                                formatPip={formatPip}
                                totalQuantity={asks[0].accQuantity}
                            />
                        ))}
                        <TopOfBookSpread bids={bids} asks={asks} activePair={activePair} formatSpread={formatSpread} />
                        {bids.map((row, i) => (
                            <OrderBookRow
                                key={i}
                                {...row}
                                type="bid"
                                formatPip={formatPip}
                                totalQuantity={bids[bids.length - 1].accQuantity}
                            />
                        ))}
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default VerticalBook;

interface OrderBookRowProps {
    price: number;
    quantity: number;
    accQuantity: number;
    totalQuantity: number;
    formatPip: (price: number) => GetPipIndexesReturn;
    type: 'ask' | 'bid';
}

const OrderBookRow = ({ price, quantity, accQuantity, totalQuantity, formatPip, type }: OrderBookRowProps) => {
    const sideColor = useMemo(() => (type === 'ask' ? 'bg-brand-green' : 'bg-brand-red'), [type]);
    const priceColor = useMemo(() => (type === 'ask' ? 'text-brand-green' : 'text-brand-red'), [type]);
    const width = useMemo(
        () => BigNumber.maximum(BigNumber(accQuantity).dividedBy(totalQuantity).times(100).toFixed(0), 0),
        [accQuantity, totalQuantity]
    );
    const defaultFormatNumber = useNumberFormatter({ ...numberFormatterConfig, minimumFractionDigits: 0 });
    const thousandsFormatNumber = useNumberFormatter({ maximumFractionDigits: 0, notation: 'compact' });
    const millionsFormatNumber = useNumberFormatter({ maximumFractionDigits: 1, notation: 'compact' });

    const { price: formattedPrice, pipIndexes } = formatPip(price);

    return (
        // group relative  w-full [&>*:hover]:bg-order-book-hover [&>*:hover~div]:bg-order-book-hover
        <div className="group">
            <div className="flex text-sm px-2 relative hover:bg-brand-neutral-700 text-brand-text">
                <div className={`flex-[1_1_33%] flex justify-end items-center z-10`}>
                    <div className={cn('w-full text-end', priceColor)}>
                        <HighlightedPips price={formattedPrice} boldIndexes={pipIndexes} fontSize="text-base" />
                    </div>
                </div>
                <div className={`flex-[1_0_33%] flex justify-end items-center z-10`}>
                    <div className="w-full text-end lowercase">
                        {formatNumber(quantity, defaultFormatNumber, thousandsFormatNumber, millionsFormatNumber)}
                    </div>
                </div>
                <div className={`flex-[1_0_33%] flex justify-end items-center z-10`}>
                    <div className="w-full text-end lowercase">
                        {formatNumber(accQuantity, defaultFormatNumber, thousandsFormatNumber, millionsFormatNumber)}
                    </div>
                </div>
                <div
                    className={cn(`right-0 top-0 h-full absolute opacity-25`, sideColor)}
                    style={{ width: `${width}%` }}
                />
            </div>
        </div>
    );
};

interface TopOfBookSpreadProps {
    asks: PriceLevel[];
    bids: PriceLevel[];
    activePair: PairMap;
    formatSpread(spread: BigNumber): string;
}

const TopOfBookSpread = ({ asks, bids, activePair, formatSpread }: TopOfBookSpreadProps) => {
    const lang = useLanguage();
    const [prev, setPrev] = useState({ asks: asks.length, bids: bids.length });

    const spreadRef = useRef<HTMLDivElement | null>(null);
    const spread = useMemo(() => BigNumber(asks[asks.length - 1].price).minus(bids[0].price), [asks, bids]);

    useEffect(() => {
        /*
           Checks if bids and asks returned are different in levels of depth,
           If it is within the non-distruptive* range of depths (in this case < 5), do nothing.
           If difference exceeds acceptable range, scroll TOB-Spread back into view.
        */
        const isNonDistruptiveDifference =
            Math.abs(prev.asks - asks.length) < 5 && Math.abs(prev.bids - bids.length) < 5;
        if (spreadRef.current && !isNonDistruptiveDifference) {
            setPrev({
                asks: asks.length,
                bids: bids.length
            });
            spreadRef.current.scrollIntoView({
                block: 'center'
            });
        }
    }, [spreadRef.current, asks, bids]);

    useEffect(() => {
        // Checks if current active pair is changed, scroll TOB-Spread into view.
        if (spreadRef.current) {
            spreadRef.current.scrollIntoView({
                block: 'center'
            });
        }
    }, [activePair]);

    return (
        <div
            ref={spreadRef}
            className="flex flex-row bg-brand-neutral-700 text-brand-neutral-400 text-sm p-0.5 justify-center my-1">
            {lang.orderBook.tobSpread}: {formatSpread(spread)}
        </div>
    );
};
